import React, { useState } from 'react';

// USCIS Notice Scanner - Step 9: Cover Letter/Response Drafting  
export const USCISStep9CoverLetter = ({ formData, setFormData, onNext, onBack }) => {
  const [generating, setGenerating] = useState(false);
  const [coverLetter, setCoverLetter] = useState(formData.coverLetter || '');
  const [customizing, setCustomizing] = useState(false);

  const generateCoverLetter = async () => {
    setGenerating(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const parsedResults = formData.parsedResults || {};
      const breakdown = formData.breakdown || {};
      const gapAnalysis = formData.gapAnalysis || {};
      
      const generatedLetter = `
${new Date().toLocaleDateString()}

United States Citizenship and Immigration Services
${parsedResults.processing_office || 'National Benefits Center'}

RE: Response to ${parsedResults.notice_type || 'Request for Evidence'}
    Case Number: ${parsedResults.case_number || 'N/A'}
    Receipt Number: ${parsedResults.receipt_number || 'N/A'}

Dear USCIS Officer,

I am writing in response to the ${parsedResults.notice_type || 'Request for Evidence'} dated [DATE], which requested additional evidence regarding my immigration application. I have carefully reviewed the request and am providing the requested evidence organized by exhibit as detailed below.

BACKGROUND SUMMARY:
I respectfully submitted my application for [APPLICATION TYPE] and received your request for additional evidence. I understand the importance of providing comprehensive documentation to demonstrate eligibility, and I have gathered substantial additional evidence to address the concerns raised.

POINT-BY-POINT RESPONSE:

${breakdown.core_issues?.map((issue, index) => `
${index + 1}. ${issue.issue}

Your request indicated concerns regarding ${issue.description.toLowerCase()}. In response, I am providing the following evidence:

${issue.actionable_items?.map((item, itemIndex) => `   ${String.fromCharCode(97 + itemIndex)}) ${item}`).join('\n') || ''}

This evidence demonstrates that our marriage is bona fide and was entered into in good faith, not for immigration purposes.
`).join('\n') || ''}

ADDITIONAL SUPPORTING EVIDENCE:
Beyond the specifically requested items, I am also including supplementary evidence that further supports our case:

- Character affidavits from friends and family who know our relationship
- Additional financial documentation showing joint responsibilities
- Photographic evidence spanning our relationship timeline
- Additional proof of cohabitation and shared life together

EXHIBIT LIST:
Please find attached a comprehensive exhibit list that organizes all submitted evidence by category and provides page references for your convenience. Each document has been clearly labeled and corresponds to the attached exhibit list.

CONCLUSION:
I respectfully submit that the evidence provided comprehensively addresses the concerns raised in your request. The documentation demonstrates that our marriage is genuine and bona fide, entered into for love and not for immigration benefits. I have provided extensive evidence of our financial commingling, cohabitation, and genuine marital relationship.

I trust that this additional evidence will allow USCIS to make a favorable determination on our application. Should you require any additional information or clarification, please do not hesitate to contact me.

Thank you for your consideration of this matter.

Respectfully submitted,

_______________________
[YOUR NAME]
[YOUR ADDRESS]
[YOUR PHONE NUMBER]
[YOUR EMAIL]

Enclosures: Evidence package with exhibit list
`;

      setCoverLetter(generatedLetter.trim());
      setFormData({ ...formData, coverLetter: generatedLetter.trim() });
    } catch (error) {
      console.error('Cover letter generation error:', error);
    }
    
    setGenerating(false);
  };

  const regenerateLetter = () => {
    setCustomizing(true);
    setTimeout(() => {
      generateCoverLetter();
      setCustomizing(false);
    }, 2000);
  };

  React.useEffect(() => {
    if (!coverLetter) {
      generateCoverLetter();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📄 Cover Letter & Response Drafting</h3>
        <p className="text-gray-400 text-lg">AI-generated professional response with legal formatting</p>
      </div>

      {generating ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Response Letter</h4>
          <p className="text-gray-400">AI is drafting a professional point-by-point response...</p>
        </div>
      ) : (
        <>
          {/* Cover Letter Editor */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xl font-semibold text-white">✏️ Professional Response Letter</h4>
              <div className="space-x-2">
                <button
                  onClick={regenerateLetter}
                  disabled={customizing}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    customizing
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {customizing ? (
                    <>
                      <span className="animate-spin inline-block mr-1">⟳</span>
                      Regenerating...
                    </>
                  ) : (
                    '🔄 Regenerate'
                  )}
                </button>
              </div>
            </div>
            
            <textarea
              value={coverLetter}
              onChange={(e) => {
                setCoverLetter(e.target.value);
                setFormData({ ...formData, coverLetter: e.target.value });
              }}
              rows="20"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
            />
            
            <div className="flex items-center justify-between mt-4">
              <div className="text-gray-400 text-sm">
                Word count: {coverLetter.split(' ').filter(word => word.length > 0).length} | 
                Characters: {coverLetter.length}
              </div>
              <div className="text-green-400 text-sm">
                ✅ Professional legal formatting applied
              </div>
            </div>
          </div>

          {/* Response Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">✨ Response Features</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Professional legal formatting</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Point-by-point issue addressing</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Case-specific details included</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Exhibit references integrated</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Persuasive legal reasoning</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Response Checklist</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">All USCIS concerns addressed</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Case background summarized</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Evidence cross-referenced</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Professional conclusion included</span>
                </li>
                <li className="flex items-center text-green-400">
                  <span className="mr-2">✓</span>
                  <span className="text-gray-300">Contact information provided</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Key Response Elements */}
          <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🎯 Key Response Elements</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-blue-400 font-medium mb-2">Legal Structure:</p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Proper case identification</li>
                  <li>• Clear issue acknowledgment</li>
                  <li>• Structured response format</li>
                  <li>• Professional conclusion</li>
                </ul>
              </div>
              
              <div>
                <p className="text-green-400 font-medium mb-2">Evidence Integration:</p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Exhibit cross-references</li>
                  <li>• Document organization</li>
                  <li>• Evidence categorization</li>
                  <li>• Supporting arguments</li>
                </ul>
              </div>
              
              <div>
                <p className="text-yellow-400 font-medium mb-2">Persuasive Elements:</p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Counter-narrative development</li>
                  <li>• Risk mitigation addressing</li>
                  <li>• Positive case presentation</li>
                  <li>• Compliance demonstration</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={generating}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !generating
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Final Review →
        </button>
      </div>
    </div>
  );
};

// USCIS Notice Scanner - Step 10: AI Final Review & Assessment
export const USCISStep10FinalReview = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [aiReview, setAiReview] = useState(formData.aiReview || null);

  const performAIReview = async () => {
    setAnalyzing(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // AI performs comprehensive review based on all collected data
      const responseDocuments = formData.responseDocuments || [];
      const documentAnalysis = formData.documentAnalysis || {};
      const gapAnalysis = formData.gapAnalysis || {};
      const savedAffidavits = formData.savedAffidavits || [];
      const coverLetter = formData.coverLetter || '';
      const parsedResults = formData.parsedResults || {};
      
      const aiAssessment = {
        overall_readiness: calculateOverallReadiness(formData),
        compliance_score: calculateComplianceScore(formData),
        response_strength: calculateResponseStrength(formData),
        automated_checks: {
          uscis_requirements: {
            status: responseDocuments.length >= 3 ? 'Complete' : 'Partial',
            score: Math.min(100, responseDocuments.length * 25),
            details: `${responseDocuments.length} responsive documents uploaded`
          },
          documentation: {
            status: (savedAffidavits.length > 0 && coverLetter.length > 500) ? 'Complete' : 'Partial',
            score: (savedAffidavits.length * 30) + (coverLetter.length > 500 ? 40 : 20),
            details: `${savedAffidavits.length} affidavits created, cover letter ${coverLetter.length > 500 ? 'comprehensive' : 'basic'}`
          },
          evidence_quality: {
            status: documentAnalysis.quality_assessment?.readability > 80 ? 'High' : 'Moderate',
            score: documentAnalysis.quality_assessment?.readability || 75,
            details: `Document quality assessment: ${documentAnalysis.quality_assessment?.readability || 75}%`
          },
          response_completeness: {
            status: (gapAnalysis.completion_status?.overall_completion || '0%').replace('%', '') > 60 ? 'Complete' : 'Partial',
            score: parseInt((gapAnalysis.completion_status?.overall_completion || '0%').replace('%', '')),
            details: `Response completion: ${gapAnalysis.completion_status?.overall_completion || '0%'}`
          }
        },
        ai_recommendations: generateAIRecommendations(formData),
        submission_readiness: determineSubmissionReadiness(formData),
        estimated_success_rate: calculateSuccessRate(formData),
        deadline_status: assessDeadlineStatus(parsedResults),
        final_score: 0 // Will be calculated
      };

      // Calculate final score based on all factors
      aiAssessment.final_score = Math.round(
        (aiAssessment.automated_checks.uscis_requirements.score * 0.3) +
        (aiAssessment.automated_checks.documentation.score * 0.25) +
        (aiAssessment.automated_checks.evidence_quality.score * 0.25) +
        (aiAssessment.automated_checks.response_completeness.score * 0.2)
      );

      setAiReview(aiAssessment);
      setFormData({ ...formData, aiReview: aiAssessment });
    } catch (error) {
      console.error('AI Review error:', error);
    }
    
    setAnalyzing(false);
  };

  React.useEffect(() => {
    if (!aiReview) {
      performAIReview();
    }
  }, []);

  // Helper functions for AI assessment
  const calculateOverallReadiness = (data) => {
    const docs = (data.responseDocuments || []).length;
    const affidavits = (data.savedAffidavits || []).length;
    const coverLength = (data.coverLetter || '').length;
    const gapCompletion = parseInt((data.gapAnalysis?.completion_status?.overall_completion || '0%').replace('%', ''));
    
    return Math.round((docs * 10) + (affidavits * 15) + (coverLength > 500 ? 20 : 10) + (gapCompletion * 0.5));
  };

  const calculateComplianceScore = (data) => {
    let score = 100;
    const docs = (data.responseDocuments || []).length;
    const affidavits = (data.savedAffidavits || []).length;
    const cover = (data.coverLetter || '').length;
    
    if (docs < 3) score -= 20;
    if (affidavits === 0) score -= 15;
    if (cover < 300) score -= 10;
    
    return Math.max(0, score);
  };

  const calculateResponseStrength = (data) => {
    const analysisScore = data.documentAnalysis?.quality_assessment?.readability || 75;
    const gapScore = parseInt((data.gapAnalysis?.completion_status?.overall_completion || '50%').replace('%', ''));
    return Math.round((analysisScore + gapScore) / 2);
  };

  const generateAIRecommendations = (data) => {
    const recommendations = [];
    const docs = (data.responseDocuments || []).length;
    const affidavits = (data.savedAffidavits || []).length;
    
    if (docs < 5) recommendations.push('Consider adding more supporting documents to strengthen your response');
    if (affidavits < 2) recommendations.push('Additional affidavits could enhance credibility of your case');
    if (!data.coverLetter || data.coverLetter.length < 500) recommendations.push('Expand cover letter with more detailed point-by-point responses');
    
    return recommendations.length > 0 ? recommendations : ['Your response package appears comprehensive and ready for submission'];
  };

  const determineSubmissionReadiness = (data) => {
    const readiness = calculateOverallReadiness(data);
    if (readiness >= 80) return 'Ready for Submission';
    if (readiness >= 60) return 'Minor Improvements Recommended';
    return 'Needs Additional Work';
  };

  const calculateSuccessRate = (data) => {
    const baseRate = 75;
    const docs = (data.responseDocuments || []).length;
    const affidavits = (data.savedAffidavits || []).length;
    const quality = data.documentAnalysis?.quality_assessment?.readability || 75;
    
    const bonus = (docs * 2) + (affidavits * 5) + ((quality - 75) / 5);
    return Math.min(95, Math.max(40, baseRate + bonus));
  };

  const assessDeadlineStatus = (parsed) => {
    const deadline = parsed.deadline || '';
    const daysLeft = parsed.response_deadline_days || 87;
    
    if (daysLeft > 30) return 'On Track';
    if (daysLeft > 14) return 'Moderate Timeline';
    return 'Urgent Submission Required';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusColor = (status) => {
    if (status.includes('Complete') || status.includes('High') || status.includes('Ready')) return 'text-green-400';
    if (status.includes('Partial') || status.includes('Moderate') || status.includes('Minor')) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🤖 AI Final Review & Assessment</h3>
        <p className="text-gray-400 text-lg">AI-powered comprehensive review of your response package</p>
      </div>

      {analyzing ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">AI Reviewing Your Response Package</h4>
          <p className="text-gray-400">Analyzing completeness, quality, and submission readiness...</p>
        </div>
      ) : aiReview && (
        <>
          {/* Overall Assessment */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl text-white mb-4">📊 Overall Assessment Score</h4>
            <div className={`text-6xl font-bold mb-2 ${getScoreColor(aiReview.final_score)}`}>
              {aiReview.final_score}%
            </div>
            <p className="text-gray-400">AI-Generated Readiness Score</p>
            <div className={`mt-4 px-4 py-2 rounded-lg inline-block ${
              aiReview.submission_readiness === 'Ready for Submission' ? 'bg-green-900/20 border border-green-500' :
              aiReview.submission_readiness === 'Minor Improvements Recommended' ? 'bg-yellow-900/20 border border-yellow-500' :
              'bg-red-900/20 border border-red-500'
            }`}>
              <p className={`font-semibold ${getStatusColor(aiReview.submission_readiness)}`}>
                {aiReview.submission_readiness}
              </p>
            </div>
          </div>

          {/* Automated Checks */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">✅ Automated Quality Checks</h4>
            <div className="space-y-4">
              {Object.entries(aiReview.automated_checks).map(([category, check]) => (
                <div key={category} className="flex items-center justify-between bg-gray-700 p-4 rounded-lg">
                  <div>
                    <p className="text-white font-medium capitalize">{category.replace(/_/g, ' ')}</p>
                    <p className="text-gray-400 text-sm">{check.details}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${getStatusColor(check.status)}`}>{check.status}</p>
                    <p className={`text-sm ${getScoreColor(check.score)}`}>{check.score}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Success Rate & Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-white mb-3">🎯 Estimated Success Rate</h4>
              <div className={`text-4xl font-bold ${getScoreColor(aiReview.estimated_success_rate)}`}>
                {aiReview.estimated_success_rate}%
              </div>
              <p className="text-gray-400 text-sm mt-2">Based on response quality and completeness</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-white mb-3">⏰ Deadline Status</h4>
              <div className={`text-xl font-bold ${getStatusColor(aiReview.deadline_status)}`}>
                {aiReview.deadline_status}
              </div>
              <p className="text-gray-400 text-sm mt-2">Timeline assessment for submission</p>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">💡 AI Recommendations</h4>
            <div className="space-y-2">
              {aiReview.ai_recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start bg-gray-700 p-3 rounded-lg">
                  <span className="text-blue-400 mr-2 mt-1">•</span>
                  <span className="text-gray-300">{recommendation}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Automatic Approval */}
          <div className="bg-green-900/20 border border-green-500 p-6 rounded-lg text-center">
            <div className="text-4xl mb-4">✅</div>
            <h4 className="text-xl font-semibold text-white mb-2">AI Review Complete</h4>
            <p className="text-gray-400 mb-4">
              Your response package has been automatically reviewed and assessed. 
              You can proceed to the final quality assurance step.
            </p>
            <p className="text-green-400 font-medium">Ready to proceed to Quality Assurance</p>
          </div>
        </>
      )}

      {/* Navigation - Always Enabled */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !analyzing
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Quality Assurance →
        </button>
      </div>
    </div>
  );
};